import React, { useEffect, useState } from 'react';
import { Layout, Table, Button, Tag, Space, Typography, Card, Spin, Popconfirm, App, Modal } from 'antd';
import { tc } from '../../i18n/common';
import axios from 'axios';
import { ArrowLeftOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Content } = Layout;
const { Title } = Typography;

const ClientTaskList = () => {
    const { message, modal } = App.useApp();
    const navigate = useNavigate();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [locale, setLocale] = useState(localStorage.getItem('locale') || 'en');

    // Listen for language changes
    useEffect(() => {
        const handleLocaleChange = () => {
            setLocale(localStorage.getItem('locale') || 'en');
        };
        window.addEventListener('locale-changed', handleLocaleChange);
        return () => window.removeEventListener('locale-changed', handleLocaleChange);
    }, []);

    const fetchTasks = async () => {
        try {
            setLoading(true);
            // axios baseURL is already /api
            const res = await axios.get('/tasks');
            setTasks(res.data);
        } catch (error) {
            console.error('Error fetching tasks:', error);
            message.error(tc('error_occurred') || 'Failed to load tasks');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTasks();
    }, []);

    const handleDelete = async (id) => {
        try {
            await axios.delete(`/tasks/${id}`);
            message.success('Task deleted');
            fetchTasks();
        } catch (error) {
            console.error('Error deleting task:', error);
            message.error('Failed to delete task');
        }
    };

    const formatDescription = (change) => {
        let text = change.key ? tc(change.key, change.params) : (change.desc || '');

        // Append Role if available (for USER_ADD/USER_REMOVE)
        if (change.params && change.params.role) {
            const roleMap = {
                'leaders': 'Leader',
                'mitarbeiter': 'Employee',
                'viewers': 'Viewer' 
            };
            const roleLabel = roleMap[change.params.role] || change.params.role;
            text += ` (${roleLabel})`;
        }

        if (change.details && change.details.formattedDiff) {
            text += `\n(${change.details.formattedDiff})`;
        }
        return text;
    };

    const exportMD = (task) => {
        if (!task || !task.changes) return;
        const content = task.changes.map(c => {
             const desc = formatDescription(c);
             return `[${c.timestamp || new Date(task.created_at).toISOString()}] ${c.type}: ${desc}`;
        }).join('\n');
        
        const timestamp = new Date(task.created_at).toISOString().split('T')[0];
        const filename = `Task_${task.id}_Changes_${timestamp}.md`;

        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    const exportXLSX = (task) => {
        if (!task || !task.changes) return;
        import('xlsx').then(XLSX => {
            const rows = task.changes.map(c => {
                 // Try to construct U101 format if context is available
                 if (c.details && c.details.exportContext) {
                      const ctx = c.details.exportContext;
                      const user = ctx.user || {};
                      const org = ctx.org || {};
                      
                      // Fallback for missing fields if only diff is provided
                      return {
                        "Person ID": user.externalId || ctx.id || "",
                        "Last Name": user.lastName || ctx.lastName || "",
                        "First Name": user.firstName || ctx.firstName || "",
                        "E-Mail": user.mail || "",
                        "Organisation ID": org.externalId || "",
                        "Organisation Name": org.name || "",
                        "Superior Organisation ID": org.parentIdExternal || ctx.parentOrgId || "", 
                        "Superior Organisation Name": org.parentName || ctx.parentOrgName || "",
                        "Role": user.jobTitle || ctx.role || "",
                        "Gender": user.gender || "",
                        "Superior ID": user.derivedSupExternalId || "",
                        "Superior Last Name": user.derivedSupLastName || "",
                        "Superior First Name": user.derivedSupFirstName || "",
                        "Language": user.language || "",
                        // Append Change Info
                        "Change Type": tc(`types.${c.type.toLowerCase()}`) || c.type,
                        "Change Details": formatDescription(c)
                      };
                 }

                 // Legacy/Simple Fallback
                 return {
                    "Type": tc(`types.${c.type.toLowerCase()}`) || c.type,
                    "Label": c.params?.name || c.label || '',
                    "Description": c.key ? tc(c.key, c.params) : (c.desc || ''),
                    "Date": c.timestamp || new Date(task.created_at).toISOString()
                };
            });

            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Task Changes");
            
            const timestamp = new Date(task.created_at).toISOString().split('T')[0];
            const filename = `Task_${task.id}_Changes_${timestamp}.xlsx`;
            
            XLSX.writeFile(workbook, filename);
        });
    };

    const columns = [
        {
            title: tc('organigram_ui.date'),
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => new Date(text).toLocaleDateString(),
        },
        {
            title: tc('tasks.description_label'),
            dataIndex: 'description',
            key: 'description',
            render: (text) => (
                <div style={{ whiteSpace: 'pre-wrap', maxHeight: 100, overflow: 'auto' }}>
                    {text}
                </div>
            )
        },
        {
            title: tc('tasks.status_label') || 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                let color = 'default';
                if (status === 'pending') color = 'orange';
                if (status === 'in_progress') color = 'blue';
                if (status === 'done') color = 'green';
                if (status === 'canceled') color = 'red';
                return (
                    <Tag color={color}>
                        {tc(`status.${status}`) || status.toUpperCase().replace('_', ' ')}
                    </Tag>
                );
            },
        },
        /* Files column removed as requested
        {
            title: 'Files',
            key: 'files',
            render: (_, record) => (
                <Space direction="vertical" size="small">
                    {record.files && record.files.map(f => (
                        <Button 
                            key={f.id} 
                            type="link" 
                            size="small" 
                            href={`/storage/${f.file_path}`} 
                            target="_blank"
                            icon={<span role="img" aria-label="file">📄</span>}
                        >
                            {f.file_name}
                        </Button>
                    ))}
                </Space>
            )
        },
        */
        {
            title: tc('tasks.downloads_label') || 'Downloads',
            key: 'downloads',
            render: (_, record) => (
                <Space direction="vertical" size="small">
                    <Button size="small" onClick={() => exportMD(record)}>{tc('export_md') || 'MD'}</Button>
                    <Button size="small" onClick={() => exportXLSX(record)}>{tc('export_excel') || 'XLSX'}</Button>
                </Space>
            )
        },
        {
            title: tc('tasks.action_label') || 'Action',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    {record.changes && (
                        <Button 
                            size="small" 
                            onClick={() => {
                                setSelectedTask(record);
                                setDetailModalVisible(true);
                            }}
                        >
                            {tc('tasks.details_btn')}
                        </Button>
                    )}
                    {record.status === 'pending' && (
                        <Popconfirm
                            title={tc('organigram_ui.delete_confirm')}
                            onConfirm={() => handleDelete(record.id)}
                            okText={tc('tasks.yes')}
                            cancelText={tc('tasks.no')}
                        >
                            <Button type="text" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    const renderChanges = (changes) => {
        if (!changes || !Array.isArray(changes)) return null;
        
        // Derive filters from data
        const uniqueTypes = [...new Set(changes.map(c => c.type))];
        const filters = uniqueTypes.map(t => ({
            text: tc(`types.${t.toLowerCase()}`) || t.replace('_', ' '),
            value: t
        }));

        const changeColumns = [
            { 
                title: tc('tasks.type_label') || 'Type', 
                dataIndex: 'type', 
                key: 'type', 
                filters: filters.length > 0 ? filters : null,
                onFilter: (value, record) => record.type.indexOf(value) === 0,
                render: t => <Tag>{tc(`types.${t.toLowerCase()}`) || t.replace('_', ' ')}</Tag> 
            },
            { 
                title: tc('tasks.label_label') || 'Label', 
                dataIndex: 'label', 
                key: 'label',
                render: (text, record) => record.params?.name || text || '-'
            },
            { 
                title: tc('tasks.description_label'), 
                dataIndex: 'desc', 
                key: 'desc',
                render: (text, record) => record.key ? tc(record.key, record.params) : (text || '-')
            }
        ];

        // Prepare data with unique keys to satisfy AntD rowKey warning (avoid index param)
        const dataSource = changes.map((c, i) => ({
            ...c,
            uniqueKey: c.id || `${c.timestamp}-${i}`
        }));

        return <Table 
            dataSource={dataSource} 
            columns={changeColumns} 
            pagination={false} 
            rowKey="uniqueKey" 
            size="small" 
        />;
    };

    return (
        <Layout style={{ minHeight: '100vh', background: '#f4f4f9' }}>
            <Content style={{ padding: '24px', maxWidth: 1200, margin: '0 auto', width: '100%' }}>
                <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/organigram')} />
                    <Title level={2} style={{ margin: 0 }}>{tc('tasks.my_tasks')}</Title>
                </div>
                
                <Card>
                    <Table
                        columns={columns}
                        dataSource={tasks}
                        rowKey="id"
                        loading={loading}
                        pagination={{ pageSize: 15, hideOnSinglePage: true }}
                    />
                </Card>

                <Modal
                    title={tc('tasks.changes_modal_title') || 'Task Changes'}
                    open={detailModalVisible}
                    onCancel={() => setDetailModalVisible(false)}
                    footer={null}
                    width={800}
                >
                    {selectedTask && renderChanges(selectedTask.changes)}
                </Modal>
            </Content>
        </Layout>
    );
};

export default ClientTaskList;
